import { Link } from "react-router-dom";
import { Facebook, Mail, Phone, MessageSquareText } from "lucide-react";
import { siteConfig, disclaimers } from "../config";
import { VERSION, BUILT_AT } from "../version";
// ReviewCta removed from passive placements — now shown only on lead-submit success.

const col = (title, links) => (
  <div>
    <h4>{title}</h4>
    <ul className="space-y-2 text-sm">
      {links.map(([txt, href, ext], i) => (
        <li key={i}>
          {ext ? (
            <a href={href} target="_blank" rel="noopener noreferrer">{txt}</a>
          ) : (
            <Link to={href}>{txt}</Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default function Footer() {
  return (
    <footer className="ssc-footer" data-testid="site-footer">
      <div className="ssc-container pt-16 pb-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        {/* Column 1 */}
        <div>
          <img
            src={siteConfig.logo.url}
            alt={siteConfig.logo.alt}
            className="h-14 w-auto mb-3"
            width="240" height="56"
          />
          <p className="text-sm mt-3">
            A better way to shop new and used vehicles in Central Ohio with honest answers, personal help, and over 15 years of vehicle-shopping experience.
          </p>
          <div className="flex gap-2 mt-4">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn-pill ssc-btn-pill-blue" data-testid="footer-text-scott">
              <MessageSquareText size={14} /> Text Scott
            </a>
            <a href={siteConfig.scott.phoneTel} className="ssc-btn-pill ssc-btn-pill-outline-dark" data-testid="footer-call-scott">
              <Phone size={14} /> Call
            </a>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a href={siteConfig.links.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook">
              <Facebook size={20} />
            </a>
            <a href={siteConfig.scott.emailMailto} aria-label="Email Scott" data-testid="footer-email">
              <Mail size={20} />
            </a>
          </div>
        </div>

        {col("New Vehicles", [
          ["All New Vehicles", "/new-vehicles"],
          ["New Cars", "/new-vehicles/cars"],
          ["New Trucks", "/new-vehicles/trucks"],
          ["New SUVs", "/new-vehicles/suvs"],
          ["New EVs", "/new-vehicles/evs"],
          ["Commercial & Work Trucks", "/new-vehicles/commercial"],
          ["Lease Help", "/lease-help"],
        ])}

        {col("Used Vehicles", [
          ["All Used Vehicles", "/used-vehicles"],
          ["Used Cars", "/used-vehicles/cars"],
          ["Used Trucks", "/used-vehicles/trucks"],
          ["Used SUVs", "/used-vehicles/suvs"],
          ["Used EVs", "/used-vehicles/evs"],
          ["Used Under $25,000", "/used-vehicles/under-25000"],
          ["Low Mileage Used", "/used-vehicles/low-mileage"],
          ["Used AWD & 4x4", "/used-vehicles/awd-4x4"],
        ])}

        {col("Finance & Trade", [
          ["Value My Trade", "/value-my-trade"],
          ["Payment Help", "/payment-help"],
          ["Build My Own Deal", "/build-my-own-deal"],
          ["Protection Products", "/protection-products"],
          ["Finance Application", siteConfig.links.financeApply, true],
          ["Apply for Financing", siteConfig.links.financeApply, true],
          ["Lease Help", "/lease-help"],
          ["Trade-In Process", "/trade-in-checklist"],
          ["Payment Calculator", "/payment-calculator"],
          ["Shop Accessories", siteConfig.links.accessories, true],
        ])}

        {col("Resources / Contact", [
          ["About Scott", "/about-scott"],
          ["FAQs", "/faq"],
          ["Buying Guide", "/car-buying-tips"],
          ["EV Help", "/ev-help"],
          ["Vehicle Alerts", "/vehicle-alerts"],
          ["Reviews", "/reviews"],
          ["Service Areas", "/chevy-sales-central-ohio"],
          ["Contact Scott", "/contact"],
          ["Compare Vehicles", "/compare"],
          ["Saved Vehicles", "/saved-vehicles"],
          ["Schedule Test Drive", "/schedule-test-drive"],
        ])}
      </div>

      <div className="ssc-container pb-8">
        {/* Review CTA intentionally removed from footer — now shown only after a real lead submit (see LeadSuccessReviewCta in App.js). */}
      </div>

      <div className="ssc-container border-t border-white/10 pt-6 pb-8 text-xs space-y-3">
        <p>{siteConfig.serviceAreaText}</p>
        <p>{disclaimers.sitewide}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-use">Terms of Use</Link>
          <Link to="/accessibility">Accessibility</Link>
          <Link to="/sitemap">Sitemap</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <div className="pt-2 text-[var(--ssc-silver)] flex flex-wrap items-center gap-2">
          <span>© {new Date().getFullYear()} Scott Sells Chevys</span>
          <span className="text-white/30">·</span>
          <span
            className="font-mono text-[10px] tracking-wider text-white/40 hover:text-white/70 transition"
            title={`Built ${BUILT_AT}`}
            data-testid="build-version"
          >
            Version {VERSION}
          </span>
          <span className="text-white/30">·</span>
          <a
            href={(() => {
              const candidate = siteConfig.scott.email && siteConfig.scott.email !== "YOUR_EMAIL"
                ? siteConfig.scott.email
                : "askscott@scottsellschevys.com";
              const subject = encodeURIComponent(`[Version ${VERSION}] Bug report — Scott Sells Chevys`);
              const body = encodeURIComponent(
                `What went wrong?\n\n\n` +
                `--- Diagnostic info (please keep) ---\n` +
                `Version: ${VERSION} (built ${BUILT_AT})\n` +
                `Page: ${typeof window !== "undefined" ? window.location.href : ""}\n` +
                `Browser: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}\n` +
                `Time: ${new Date().toISOString()}\n` +
                `Viewport: ${typeof window !== "undefined" ? `${window.innerWidth}×${window.innerHeight}` : ""}`
              );
              return `mailto:${candidate}?subject=${subject}&body=${body}`;
            })()}
            className="font-mono text-[10px] tracking-wider text-white/40 hover:text-white/70 transition underline-offset-2 hover:underline"
            data-testid="report-bug-link"
          >
            Report a bug
          </a>
        </div>
      </div>
    </footer>
  );
}
